import type { CreateAiChatClientOptions } from '@leagueplatform/ai';

// Which kit and which simulated member a demo runs as. Stored rather than held in
// state: the choice has to outlive the modal, the chat surface and a reload.

const BUNDLE_ID_STORAGE_KEY = 'chatBundleId';
const PERSONA_ID_STORAGE_KEY = 'chatPersonaId';

export type DemoSelection = { kitId: string; personaId: string };

const readStored = (key: string): string => {
  try {
    return window.localStorage.getItem(key)?.trim() ?? '';
  } catch {
    // Storage can be blocked — no selection, so the tenant default serves.
    return '';
  }
};

const writeStored = (key: string, value: string) => {
  try {
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // As above.
  }
};

export const readDemoSelection = (): DemoSelection => ({
  kitId: readStored(BUNDLE_ID_STORAGE_KEY),
  personaId: readStored(PERSONA_ID_STORAGE_KEY),
});

// The chat SDK hands the whole request to a host before sending it. Which kit and
// persona serve a run is ours to write, and reading the choice here rather than
// capturing it means a new selection applies to the next message.
export const transformRunRequest: NonNullable<
  CreateAiChatClientOptions['transformRunRequest']
> = (request) => {
  const { kitId, personaId } = readDemoSelection();

  return {
    ...request,
    // Top level and snake_case: that is the field the server reads.
    ...(kitId ? { bundle_id: kitId } : {}),
    ...(personaId
      ? { forwardedProps: { ...request.forwardedProps, personaId } }
      : {}),
  };
};

export const writeDemoSelection = ({ kitId, personaId }: DemoSelection) => {
  writeStored(BUNDLE_ID_STORAGE_KEY, kitId);
  writeStored(PERSONA_ID_STORAGE_KEY, personaId);
};
